import { CommandPalette } from './CommandPalette';
import { useRegisteredPaletteActions } from './palette-registry';
import { useCommandActions } from './useCommandActions';
import { useSettingCommands } from './useSettingCommands';

import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { ReactElement } from 'react';

/**
 * The app's one ⌘K palette, assembled from its three sources: the global actions
 * (`useCommandActions`), whatever the mounted page has published
 * (`useRegisterPaletteActions`), and the sidebar's destinations.
 *
 * A component rather than inline in `AppShell` because the registry's consumer
 * has to sit INSIDE its provider, and the provider wraps the shell. It also keeps
 * `AppShell` under the file-size ceiling, which is why `useCommandActions` was
 * split out already.
 *
 * Page actions come FIRST: on a page that publishes commands, "what can I do
 * here" is a better guess at intent than "where else can I go". The per-setting
 * jumps come LAST of the actions: there are enough of them that leading with
 * them would bury the two-or-three commands an unfiltered palette should show —
 * but they are the whole point once anything is typed, and each one carries the
 * keyword bag from the shared index, so "delete after days" finds the retention
 * card even though neither word is in its heading.
 *
 * The nav rows are passed DOWN to `useSettingCommands` as well as across to the
 * palette, because the shared index builds each setting's breadcrumb from them:
 * a renamed nav row renames the trail with it, and no second table can drift.
 */
export function ShellCommandPalette({
  open,
  onOpenChange,
  items,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly items: readonly SidebarItem[];
}): ReactElement {
  const globalActions = useCommandActions();
  const settingCommands = useSettingCommands(items);
  const pageActions = useRegisteredPaletteActions();

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      items={items}
      actions={[...pageActions, ...globalActions, ...settingCommands]}
    />
  );
}
